import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  MailQuestion,
  MessageSquareReply,
  RotateCcw,
  Send,
  UserCheck,
} from 'lucide-react';
import {
  LifecycleLog,
  LifecycleQuestions,
  OrderResponse,
  ReplyDraft,
  orderService,
} from '../../../services/OrderService';
import StructuredQuestionForm from '../chat/StructuredQuestionForm';
import type { StructuredAnswer } from '../chat/types';
import { ATTENTION_TOKENS, channelOf, formatDwell, hoursSince, isUnowned } from './orderLifecycle';

type Panel = 'questions' | 'reply' | 'log' | 'cancel' | null;

interface Props {
  order: OrderResponse;
  onUpdated: (order: OrderResponse) => void;
  onOpen: () => void;
  /** Number of table columns to span (desktop) — omit for the card layout. */
  colSpan?: number;
  currentUid?: string | null;
  /** Rendered inside the order itself — "Open the order" buttons are dropped. */
  inOrder?: boolean;
}

const d = (order: OrderResponse): Record<string, any> => (order.attention_details as any) || {};

export const useAttentionCopy = (order: OrderResponse) => {
  const { t } = useTranslation();
  const flag = order.attention_flag!;
  const reason = order.attention_reason || '';
  const det = d(order);
  const ident = order.external_order_id || `#${order.id}`;
  const vin = order.vin || '';
  const h = hoursSince(det.raised_at || order.attention_changed_at);
  const ago = formatDwell(h, t);

  let headline = '';
  let explanation = '';
  const meta: string[] = [];

  if (flag === 'system_error') {
    const code = det.error_code || 'ERR_PIPELINE';
    const attempts = det.attempts ?? 1;
    const max = det.max_attempts ?? 3;
    if (det.final) {
      headline = t('orders.strip.system_error.finalHeadline', { defaultValue: '{{code}} — {{n}} attempts failed', code, n: attempts });
      explanation = t('orders.strip.system_error.finalExplanation', { defaultValue: 'CATA failed, not the request. Support has been notified. Do not contact the customer — nothing on their side is wrong.' });
    } else {
      headline = t('orders.strip.system_error.retryHeadline', { defaultValue: '{{what}} is unreachable', what: code === 'ERR_EPC_TIMEOUT' || code === 'ERR_EPC_UNREACHABLE' ? t('orders.strip.system_error.catalog', { defaultValue: 'Parts catalog' }) : t('orders.strip.system_error.pipeline', { defaultValue: 'The pipeline' }) });
      const next = det.next_retry_at ? Math.max(0, Math.round((new Date(det.next_retry_at).getTime() - Date.now()) / 60000)) : null;
      explanation = t('orders.strip.system_error.retryExplanation', { defaultValue: 'Retrying automatically{{when}} · attempt {{n}} of {{max}}. Do not contact the customer.', when: next !== null ? t('orders.strip.system_error.inMinutes', { defaultValue: ' in {{m}} min', m: next }) : '', n: attempts, max });
    }
    meta.push(code);
    meta.push(`${attempts}/${max}`);
  } else if (flag === 'needs_customer') {
    const copy: Record<string, [string, string]> = {
      no_vehicle: ['The message has no VIN, plate or model', "We can't identify the vehicle — only the sender can supply it."],
      invalid_vin: [det.vin ? `${det.vin} failed to decode` : 'The VIN failed to decode', det.message || 'It does not decode in any connected catalog — ask the sender to check it.'],
      vin_mismatch: ['VIN and vehicle description disagree', 'Ask the sender which vehicle the parts are for.'],
      unreadable_attachment: [det.filename ? `${det.filename} could not be read` : 'An attachment could not be read', 'Encrypted, corrupt or too blurred — ask the sender to resend it unlocked.'],
      ambiguous_request: ['The request is ambiguous', 'Nothing we hold resolves it — the sender has to clarify.'],
    };
    const [hl, ex] = copy[reason] || ['The customer needs to answer', 'Only the sender can unblock this order.'];
    headline = t(`orders.strip.needs_customer.${reason}.headline`, { defaultValue: hl, vin: det.vin, filename: det.filename });
    explanation = t(`orders.strip.needs_customer.${reason}.explanation`, { defaultValue: ex, message: det.message });
    if (ago) meta.push(t('orders.strip.meta.flaggedAgo', { defaultValue: 'flagged {{ago}} ago', ago }));
  } else if (flag === 'waiting_customer') {
    const sentH = hoursSince(det.sent_at || order.attention_changed_at);
    headline = t('orders.strip.waiting_customer.headline', { defaultValue: 'Waiting on the customer' });
    explanation = t('orders.strip.waiting_customer.explanation', { defaultValue: 'Reply sent on {{channel}} — no answer yet. Escalates back to you after 48h.', channel: det.channel || channelOf(order) });
    if (sentH !== null) meta.push(t('orders.strip.meta.askedAgo', { defaultValue: 'asked {{ago}} ago', ago: formatDwell(sentH, t) }));
    if (det.reminder_sent_at) meta.push(t('orders.strip.meta.reminderSent', { defaultValue: 'reminder sent' }));
  } else {
    const qn = det.question_count ?? (det.questions?.length || 0);
    const rejected = Array.isArray(det.rejected) ? det.rejected.length : 0;
    const copy: Record<string, [string, string]> = {
      assistance_requested: ['The agent needs a decision', det.summary || 'It handed the order over rather than guess — take it to review the candidates.'],
      // No generic sentence for internal questions: the strip shows the actual
      // question summary when the backend supplied one, and nothing otherwise.
      questions_internal: [`${qn || 'Some'} question${qn === 1 ? '' : 's'} need your answer`, ''],
      // Stage 3b: the photo was read and still could not separate the catalog
      // candidates — the deciding difference (when found) is what to check.
      cannot_separate: ['Pick the exact part', 'The photo could not separate the catalog candidates — check them on the drawing.'],
      missing_field_internal: ['A field is missing', det.summary || 'The data is in your system — fill it in to continue.'],
      review_sla: ['Results have been waiting', `Matched parts have sat unreviewed for over ${det.sla_hours ?? 4}h.`],
      cart_sla: ['Cart not submitted', `Lines have sat in the cart for over ${det.sla_hours ?? 24}h.`],
      supplier_rejection: [`${det.supplier || 'The supplier'} rejected ${rejected} line${rejected === 1 ? '' : 's'}`, 'Someone must decide what happens to the rejected lines.'],
      customer_silent: ['No reply from the customer', `${det.silent_hours ?? 48}h of silence after our message — decide how to proceed.`],
    };
    const [hl, ex] = copy[reason] || ['Needs your attention', 'Someone here can settle this.'];
    headline = t(`orders.strip.needs_you.${reason}.headline`, { defaultValue: hl, n: qn, supplier: det.supplier, rejected });
    const deciding = (det.questions || []).map((q: any) => q?.deciding_difference).find(Boolean);
    explanation = reason === 'questions_internal'
      ? (det.summary || '')
      : reason === 'cannot_separate' && deciding
        ? t('orders.strip.needs_you.cannot_separate.deciding', { defaultValue: 'Check: {{diff}}', diff: deciding })
      : t(`orders.strip.needs_you.${reason}.explanation`, { defaultValue: ex, hours: det.sla_hours, silent: det.silent_hours });
    if (det.customer_reason) {
      explanation += ' ' + t('orders.strip.needs_you.noReplyChannel', { defaultValue: '(This is a walk-in order — there is no thread to reply on.)' });
    }
    if (ago) meta.push(['review_sla', 'cart_sla', 'customer_silent'].includes(reason)
      ? t('orders.strip.meta.flaggedAgo', { defaultValue: 'flagged {{ago}} ago', ago })
      : t('orders.strip.meta.handedOverAgo', { defaultValue: 'handed over {{ago}} ago', ago }));
  }
  if (vin && flag !== 'needs_customer') meta.push(vin);
  meta.push(ident);
  return { headline, explanation, meta };
};

const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'ghost'; fg: string; busy?: boolean }> = ({
  tone = 'ghost', fg, busy, children, className = '', ...rest
}) => (
  <button
    type="button"
    {...rest}
    disabled={rest.disabled || busy}
    onClick={(e) => { e.stopPropagation(); rest.onClick?.(e); }}
    className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap disabled:opacity-60 disabled:cursor-wait ${tone === 'primary' ? 'text-white' : 'bg-white/70 border'} ${className}`}
    style={tone === 'primary' ? { background: fg } : { borderColor: fg, color: fg }}
  >
    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
    {children}
  </button>
);

const OrderAttentionStrip: React.FC<Props> = ({ order, onUpdated, onOpen, colSpan, currentUid, inOrder = false }) => {
  const { t, i18n } = useTranslation();
  const flag = order.attention_flag;
  const tokens = flag ? ATTENTION_TOKENS[flag] : null;
  const { headline, explanation, meta } = useAttentionCopy(order);
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<LifecycleQuestions | null>(null);
  const [draft, setDraft] = useState<ReplyDraft | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [log, setLog] = useState<LifecycleLog | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const reason = order.attention_reason || '';
  const unowned = isUnowned(order);
  const mine = order.owner_type === 'person' && !!currentUid && order.owner_uid === currentUid;

  const run = useCallback(async (key: string, fn: () => Promise<OrderResponse | void>) => {
    setBusy(key); setError(null);
    try {
      const updated = await fn();
      if (updated) onUpdated(updated);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || t('orders.strip.failed', { defaultValue: 'That did not work — try again.' }));
    } finally {
      setBusy(null);
    }
  }, [onUpdated, t]);

  const openPanel = useCallback(async (p: Panel) => {
    setError(null);
    if (panel === p) { setPanel(null); return; }
    setPanel(p);
    if (!order.id) return;
    try {
      if (p === 'questions' && !questions) setQuestions(await orderService.getLifecycleQuestions(order.id));
      if (p === 'reply' && !draft) {
        const dr = await orderService.getReplyDraft(order.id);
        setDraft(dr); setReplyBody(dr.body);
      }
      if (p === 'log') setLog(await orderService.getLifecycleLog(order.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not load');
    }
  }, [panel, order.id, questions, draft]);

  // Reset panels when the flag changes (after an action).
  useEffect(() => { setPanel(null); setQuestions(null); setDraft(null); setLog(null); }, [flag, reason]);

  if (!flag || !tokens || !order.id) return null;
  const id = order.id;
  const fg = tokens.fg;
  const canReply = ['email', 'whatsapp', 'web'].includes(channelOf(order));

  const Icon = flag === 'system_error' ? AlertOctagon : flag === 'waiting_customer' ? Clock : flag === 'needs_customer' ? MailQuestion : AlertTriangle;

  // ── Fix buttons per flag / reason (spec §4) ────────────────────────────
  const actions: React.ReactNode[] = [];
  const takeBtn = unowned ? (
    <Btn key="take" tone="primary" fg={fg} busy={busy === 'take'} onClick={() => run('take', () => orderService.lifecycleTake(id))}>
      <UserCheck className="h-3 w-3" />{t('orders.strip.actions.takeIt', { defaultValue: 'Take it' })}
    </Btn>
  ) : null;

  if (flag === 'system_error') {
    actions.push(
      <Btn key="log" fg={fg} onClick={() => openPanel('log')}>
        <FileText className="h-3 w-3" />{t('orders.strip.actions.viewLog', { defaultValue: 'View log' })}
        {panel === 'log' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </Btn>,
      <Btn key="retry" tone="primary" fg={fg} busy={busy === 'retry'} onClick={() => run('retry', () => orderService.lifecycleRetry(id))}>
        <RotateCcw className="h-3 w-3" />{t('orders.strip.actions.retryNow', { defaultValue: 'Retry now' })}
      </Btn>,
    );
  } else if (flag === 'needs_customer') {
    actions.push(
      <Btn key="reply" tone="primary" fg={fg} onClick={() => openPanel('reply')}>
        <MessageSquareReply className="h-3 w-3" />{t('orders.strip.actions.replyToSender', { defaultValue: 'Reply to sender' })}
      </Btn>,
      <Btn key="self" fg={fg} busy={busy === 'self'} onClick={() => run('self', () => orderService.lifecycleResolveInternally(id))}>
        {t('orders.strip.actions.haveAnswer', { defaultValue: 'I have the answer myself' })}
      </Btn>,
    );
  } else if (flag === 'waiting_customer') {
    actions.push(
      <Btn key="answered" tone="primary" fg={fg} busy={busy === 'answered'} onClick={() => run('answered', () => orderService.lifecycleMarkAnswered(id))}>
        <CheckCircle2 className="h-3 w-3" />{t('orders.strip.actions.customerAnswered', { defaultValue: 'Customer answered' })}
      </Btn>,
      <Btn key="remind" fg={fg} onClick={() => openPanel('reply')}>
        <Send className="h-3 w-3" />{t('orders.strip.actions.sendReminder', { defaultValue: 'Send a reminder' })}
      </Btn>,
    );
  } else {
    // needs_you
    if (reason === 'questions_internal' || reason === 'cannot_separate' || reason === 'assistance_requested') {
      if (takeBtn) actions.push(takeBtn);
      // Inside the order the question form already sits in the conversation
      // itself, so the strip's answer toggle would just duplicate it.
      if (!inOrder) actions.push(
        <Btn key="answer" tone={unowned ? 'ghost' : 'primary'} fg={fg} onClick={() => openPanel('questions')}>
          {reason === 'assistance_requested'
            ? t('orders.strip.actions.review', { defaultValue: 'Review candidates' })
            : t('orders.strip.actions.answerQuestions', { defaultValue: 'Answer questions' })}
          {panel === 'questions' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Btn>,
      );
      if (canReply) {
        actions.push(
          <Btn key="ask" fg={fg} busy={busy === 'ask'} onClick={() => run('ask', () => orderService.lifecycleAskCustomer(id))}>
            <MailQuestion className="h-3 w-3" />{t('orders.strip.actions.askCustomer', { defaultValue: 'Ask the customer instead' })}
          </Btn>,
        );
      }
      // Already answered, or stale on an older order: close it here. Clears the
      // question in the chat as well — the flag alone would come back on reopen.
      if (reason === 'questions_internal' || reason === 'cannot_separate') {
        actions.push(
          <Btn key="dismissq" tone="ghost" fg={fg} busy={busy === 'dismissq'} onClick={() => run('dismissq', () => orderService.lifecycleDismissQuestion(id))}>
            {t('orders.strip.actions.dismissQuestion', { defaultValue: 'Dismiss question' })}
          </Btn>,
        );
      }
    } else if (reason === 'customer_silent') {
      if (takeBtn) actions.push(takeBtn);
      actions.push(
        <Btn key="remind" tone="primary" fg={fg} onClick={() => openPanel('reply')}>
          <Send className="h-3 w-3" />{t('orders.strip.actions.replyAgain', { defaultValue: 'Message again' })}
        </Btn>,
        <Btn key="answered" fg={fg} busy={busy === 'answered'} onClick={() => run('answered', () => orderService.lifecycleMarkAnswered(id))}>
          {t('orders.strip.actions.customerAnswered', { defaultValue: 'Customer answered' })}
        </Btn>,
        <Btn key="cancel" fg={fg} onClick={() => openPanel('cancel')}>
          {t('orders.strip.actions.cancelOrder', { defaultValue: 'Cancel order' })}
        </Btn>,
      );
    } else if (reason === 'supplier_rejection') {
      if (takeBtn) actions.push(takeBtn);
      if (!inOrder) actions.push(
        <Btn key="open" tone="primary" fg={fg} onClick={onOpen}>
          <ExternalLink className="h-3 w-3" />{t('orders.strip.actions.openOrder', { defaultValue: 'Open the order' })}
        </Btn>,
      );
      actions.push(
        <Btn key="ack" tone={inOrder ? 'primary' : 'ghost'} fg={fg} busy={busy === 'ack'} onClick={() => run('ack', () => orderService.lifecycleResolveInternally(id))}>
          {t('orders.strip.actions.acknowledge', { defaultValue: 'Handled' })}
        </Btn>,
      );
    } else {
      // review_sla / cart_sla / missing_field_internal / fallback
      if (takeBtn) actions.push(takeBtn);
      if (!inOrder) actions.push(
        <Btn key="open" tone="primary" fg={fg} onClick={onOpen}>
          <ExternalLink className="h-3 w-3" />{t('orders.strip.actions.openOrder', { defaultValue: 'Open the order' })}
        </Btn>,
      );
      actions.push(
        <Btn key="ack" tone={inOrder ? 'primary' : 'ghost'} fg={fg} busy={busy === 'ack'} onClick={() => run('ack', () => orderService.lifecycleResolveInternally(id))}>
          {t('orders.strip.actions.dismiss', { defaultValue: 'Dismiss' })}
        </Btn>,
      );
    }
  }

  const submitAnswers = async (message: string, structured: StructuredAnswer) => {
    await run('answer', async () => {
      await orderService.answerLifecycleQuestions(id, structured.answers as any[], message, i18n.language);
      // The answer turn runs in the background; the flag is cleared server-side immediately.
      return { ...order, attention_flag: null, attention_reason: null, attention_details: null, stage: 'analysing' } as OrderResponse;
    });
  };

  const sendReply = () => run('reply', () => orderService.lifecycleReply(id, replyBody, draft?.subject));

  const body = (
    <div
      className="w-full"
      style={{ background: tokens.strip, borderLeft: `4px solid ${tokens.rail}` }}
      onClick={(e) => e.stopPropagation()}
      role="region"
      aria-label={headline}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
        <Icon className="h-4 w-4 flex-shrink-0" style={{ color: tokens.rail === '#C9C2FF' ? '#5B5872' : tokens.rail }} aria-hidden="true" />
        <span className="text-[13px] font-semibold" style={{ color: fg }}>{headline}</span>
        {explanation ? <span className="text-[12px] text-[#6B6862]">— {explanation}</span> : null}
        <span className="ml-auto flex items-center gap-2 text-[11px] text-[#6B6862] font-mono">
          {meta.map((m, i) => <span key={i}>{m}</span>)}
        </span>
        <span className="flex items-center gap-1.5">{actions}</span>
      </div>
      {error && <p className="px-3 pb-2 text-[12px] text-[#B42318]">{error}</p>}

      {/* ── Inline panels ─────────────────────────────────────────────── */}
      {panel === 'questions' && (
        <div className="mx-3 mb-3 rounded-lg border bg-white p-3" style={{ borderColor: tokens.rail }}>
          {!questions ? (
            <div className="flex items-center gap-2 text-[12px] text-[#6B6862]"><Loader2 className="h-3 w-3 animate-spin" />{t('orders.strip.loading', { defaultValue: 'Loading…' })}</div>
          ) : questions.questions.length === 0 ? (
            <div className="text-[12px] text-[#6B6862]">
              {t('orders.strip.noQuestions', { defaultValue: 'The question is no longer pending — open the order to see where it stands.' })}
              {!inOrder && <Btn fg={fg} className="ml-2" onClick={onOpen}><ExternalLink className="h-3 w-3" />{t('orders.strip.actions.openOrder', { defaultValue: 'Open the order' })}</Btn>}
            </div>
          ) : (
            <>
              {/* No `questions.message` here — it restates the question the form
                  below already asks, so it read as a duplicated message. */}
              {questions.resolved_parts.length > 0 && (
                <p className="mb-2 text-[11px] text-[#6B6862]">
                  {t('orders.strip.alreadyResolved', { defaultValue: 'Already matched: {{parts}}', parts: questions.resolved_parts.map(p => p.name).filter(Boolean).join(', ') })}
                </p>
              )}
              <StructuredQuestionForm questions={questions.questions} t={t as any} onSubmit={submitAnswers} disabled={busy === 'answer'} />
            </>
          )}
        </div>
      )}

      {panel === 'reply' && (
        <div className="mx-3 mb-3 rounded-lg border bg-white p-3" style={{ borderColor: tokens.rail }}>
          {!draft ? (
            <div className="flex items-center gap-2 text-[12px] text-[#6B6862]"><Loader2 className="h-3 w-3 animate-spin" />{t('orders.strip.drafting', { defaultValue: 'Drafting the reply…' })}</div>
          ) : (
            <>
              <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-[#6B6862]">
                <span>{t('orders.strip.replyVia', { defaultValue: 'Via {{channel}}', channel: draft.channel })}</span>
                {draft.to && <span className="font-mono">→ {draft.to}</span>}
                <span>· {draft.language}</span>
                {!draft.can_send && <span className="text-[#B42318]">{t('orders.strip.noReplyChannel', { defaultValue: 'No reply channel on this order' })}</span>}
              </div>
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-gray-200 p-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#6866ff]"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Btn tone="primary" fg={fg} busy={busy === 'reply'} disabled={!draft.can_send || !replyBody.trim()} onClick={sendReply}>
                  <Send className="h-3 w-3" />{flag === 'waiting_customer' ? t('orders.strip.actions.sendReminder', { defaultValue: 'Send a reminder' }) : t('orders.strip.actions.sendReply', { defaultValue: 'Send reply' })}
                </Btn>
                {flag !== 'waiting_customer' && (
                  <Btn fg={fg} busy={busy === 'self'} onClick={() => run('self', () => orderService.lifecycleResolveInternally(id))}>
                    {t('orders.strip.actions.haveAnswer', { defaultValue: 'I have the answer myself' })}
                  </Btn>
                )}
                <Btn fg={fg} onClick={() => setPanel(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Btn>
              </div>
            </>
          )}
        </div>
      )}

      {panel === 'log' && (
        <div className="mx-3 mb-3 rounded-lg border bg-white p-3" style={{ borderColor: tokens.rail }}>
          {!log ? (
            <div className="flex items-center gap-2 text-[12px] text-[#6B6862]"><Loader2 className="h-3 w-3 animate-spin" />{t('orders.strip.loading', { defaultValue: 'Loading…' })}</div>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap gap-3 text-[11px] text-[#6B6862] font-mono">
                <span>{log.error_code}</span>
                <span>{t('orders.strip.log.attempts', { defaultValue: 'attempts {{n}}/{{max}}', n: log.attempts ?? 0, max: log.max_attempts ?? 3 })}</span>
                {log.next_retry_at && !log.final && <span>{t('orders.strip.log.next', { defaultValue: 'next {{at}}', at: new Date(log.next_retry_at).toLocaleTimeString() })}</span>}
                <span>{t('orders.strip.log.heldStage', { defaultValue: 'held stage: {{stage}}', stage: log.stage })}</span>
              </div>
              <pre className="max-h-56 overflow-auto rounded-md bg-[#1F1D3A] p-2 text-[11px] leading-snug text-[#E5E2FF] whitespace-pre-wrap">
                {log.log.length === 0 ? (log.message || '—') : log.log.map((l) => `[${l.ts || ''}] attempt ${l.attempt ?? ''}: ${l.message || ''}${l.traceback ? '\n' + l.traceback : ''}`).join('\n\n')}
              </pre>
              <div className="mt-2 flex gap-2">
                <Btn tone="primary" fg={fg} busy={busy === 'retry'} onClick={() => run('retry', () => orderService.lifecycleRetry(id))}>
                  <RotateCcw className="h-3 w-3" />{t('orders.strip.actions.retryNow', { defaultValue: 'Retry now' })}
                </Btn>
                {!inOrder && <Btn fg={fg} onClick={onOpen}><ExternalLink className="h-3 w-3" />{t('orders.strip.actions.openOrder', { defaultValue: 'Open the order' })}</Btn>}
              </div>
            </>
          )}
        </div>
      )}

      {panel === 'cancel' && (
        <div className="mx-3 mb-3 rounded-lg border bg-white p-3" style={{ borderColor: tokens.rail }}>
          <label className="block text-[12px] text-[#6B6862] mb-1">{t('orders.strip.cancelReason', { defaultValue: 'Why is this order being cancelled?' })}</label>
          <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full rounded-md border border-gray-200 p-2 text-[13px]" />
          <div className="mt-2 flex gap-2">
            <Btn tone="primary" fg="#B42318" busy={busy === 'cancel'} disabled={!cancelReason.trim()} onClick={() => run('cancel', () => orderService.lifecycleCancel(id, cancelReason.trim()))}>
              {t('orders.strip.actions.confirmCancel', { defaultValue: 'Cancel the order' })}
            </Btn>
            <Btn fg={fg} onClick={() => setPanel(null)}>{t('common.back', { defaultValue: 'Back' })}</Btn>
          </div>
        </div>
      )}
      {mine && flag !== 'system_error' && (
        <span className="sr-only">{t('orders.strip.yours', { defaultValue: 'You own this order' })}</span>
      )}
    </div>
  );

  if (colSpan) {
    return (
      <tr onClick={(e) => e.stopPropagation()}>
        <td colSpan={colSpan} className="p-0">{body}</td>
      </tr>
    );
  }
  return body;
};

export default OrderAttentionStrip;
